import { useState } from 'react';
import {
  QuestionMarkCircleIcon,
  KeyIcon,
  MapPinIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/Card';

interface AccordionItemProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function AccordionItem({ title, icon: Icon, children, defaultOpen = false }: AccordionItemProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-secondary-50 dark:bg-secondary-700/50 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
      >
        <div className="flex items-center">
          <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-3" />
          <span className="font-medium text-secondary-900 dark:text-secondary-100">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 py-4 bg-white dark:bg-secondary-800">
          {children}
        </div>
      )}
    </div>
  );
}

export default function Help() {
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center mb-6">
        <QuestionMarkCircleIcon className="w-8 h-8 text-primary-600 dark:text-primary-400 mr-3" />
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Help & Documentation</h1>
      </div>

      {/* Quick Start */}
      <Card>
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">Quick Start</h2>
        <p className="text-secondary-600 dark:text-secondary-400 mb-4">
          KeyKeeper is a key management system for tracking physical keys, their assignments, and inventory across locations.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
            <h3 className="font-medium text-primary-900 dark:text-primary-200 mb-2">Getting Started</h3>
            <ol className="text-sm text-primary-800 dark:text-primary-300 space-y-1 list-decimal list-inside">
              <li>Add your locations (buildings/sites)</li>
              <li>Add key inventory items</li>
              <li>Add people who will be assigned keys</li>
              <li>Assign keys to people as needed</li>
            </ol>
          </div>
          <div className="p-4 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
            <h3 className="font-medium text-secondary-900 dark:text-secondary-100 mb-2">Key Features</h3>
            <ul className="text-sm text-secondary-700 dark:text-secondary-300 space-y-1 list-disc list-inside">
              <li>Track key inventory by location</li>
              <li>Assign and return keys</li>
              <li>View assignment history</li>
              <li>Export and import data</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Feature Documentation */}
      <Card>
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">Features</h2>
        <div className="space-y-3">
          <AccordionItem title="Key Inventory" icon={KeyIcon} defaultOpen>
            <div className="space-y-3 text-sm text-secondary-700 dark:text-secondary-300">
              <p>
                The Key Inventory page displays all keys in the system, organized by location and key type.
              </p>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100">Adding Keys</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Click "Add Key" button</li>
                <li>Select the location where the key is stored</li>
                <li>Select the key type</li>
                <li>Enter the quantity available</li>
                <li>Add any notes (optional)</li>
                <li>Click Save</li>
              </ol>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100 mt-3">Assigning Keys</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Find the key in the inventory list</li>
                <li>Click the "Assign" button</li>
                <li>Search for and select the person</li>
                <li>Enter the quantity to assign</li>
                <li>Add notes if needed</li>
                <li>Confirm the assignment</li>
              </ol>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100 mt-3">Returning Keys</h4>
              <p>
                Go to the People page, find the person, view their assignments, and click "Return" on the key to mark it as returned.
              </p>
            </div>
          </AccordionItem>

          <AccordionItem title="Locations" icon={MapPinIcon}>
            <div className="space-y-3 text-sm text-secondary-700 dark:text-secondary-300">
              <p>
                Locations represent physical sites or buildings where keys are managed. Each key inventory item belongs to a location.
              </p>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100">Viewing Locations</h4>
              <p>
                The Locations page shows all configured locations with their address information.
              </p>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100 mt-3">Managing Locations</h4>
              <p>
                Administrators can add, edit, and delete locations directly from the Locations page. Changes are saved to the database immediately.
              </p>
            </div>
          </AccordionItem>

          <AccordionItem title="People" icon={UserGroupIcon}>
            <div className="space-y-3 text-sm text-secondary-700 dark:text-secondary-300">
              <p>
                People are individuals who can be assigned keys. They can be added manually or synced from Azure AD.
              </p>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100">Adding People</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Click "Add Person" button</li>
                <li>Enter their name (required)</li>
                <li>Enter email and phone (optional)</li>
                <li>Select their department (optional)</li>
                <li>Click Save</li>
              </ol>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100 mt-3">Azure AD Sync</h4>
              <p>
                If configured, you can sync people from Azure Active Directory. This imports user information automatically.
              </p>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100 mt-3">Viewing Assignments</h4>
              <p>
                Click on a person to see all keys currently assigned to them and their assignment history.
              </p>
            </div>
          </AccordionItem>

          <AccordionItem title="User Management" icon={Cog6ToothIcon}>
            <div className="space-y-3 text-sm text-secondary-700 dark:text-secondary-300">
              <p>
                Users are system accounts that can log in and manage KeyKeeper. All users must be added from Azure Active Directory.
              </p>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100">Adding Users from Azure AD</h4>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Go to Settings &gt; Users</li>
                <li>Click "Add from Azure AD"</li>
                <li>Search for the user by name or email</li>
                <li>Select the user from the search results</li>
                <li>Assign a role (User, Site Admin, or Admin)</li>
                <li>For Site Admin role, select which locations they can access</li>
                <li>Click "Add User"</li>
              </ol>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100 mt-3">User Roles</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Admin</strong>: Full access to all features and all locations</li>
                <li><strong>Site Admin</strong>: Can manage keys and people at assigned locations only</li>
                <li><strong>User</strong>: Read-only access to view inventory and assignments</li>
              </ul>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100 mt-3">Editing Users</h4>
              <p>
                Click "Edit" on any user card to change their role or location access. Note: The last admin cannot have their role changed.
              </p>
            </div>
          </AccordionItem>

          <AccordionItem title="Settings & Backup" icon={Cog6ToothIcon}>
            <div className="space-y-3 text-sm text-secondary-700 dark:text-secondary-300">
              <p>
                The Settings page provides access to system configuration and data management options.
              </p>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100">Export Data</h4>
              <p>
                Export all application data to a JSON file for backup purposes. This includes locations, key types, inventory, people, and assignments.
              </p>
              <h4 className="font-medium text-secondary-900 dark:text-secondary-100 mt-3">Import Data</h4>
              <p>
                Restore data from a previously exported backup file. This will merge with existing data.
              </p>
            </div>
          </AccordionItem>
        </div>
      </Card>

      {/* Configuration */}
      <Card>
        <div className="flex items-center mb-4">
          <DocumentTextIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2" />
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">Configuration</h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-secondary-900 dark:text-secondary-100 mb-2">Managing Key Types</h3>
            <p className="text-sm text-secondary-700 dark:text-secondary-300 mb-2">
              Key types (e.g., "AAA 5", "Fork Lift Key") can be managed from the <strong>Key Types</strong> page in the left menu. Administrators can:
            </p>
            <ul className="text-sm text-secondary-700 dark:text-secondary-300 list-disc list-inside ml-2">
              <li>Add new key types with a name and optional description</li>
              <li>Edit existing key types</li>
              <li>Delete key types that are no longer needed</li>
            </ul>
          </div>

          <div>
            <h3 className="font-medium text-secondary-900 dark:text-secondary-100 mb-2">Managing Locations</h3>
            <p className="text-sm text-secondary-700 dark:text-secondary-300 mb-2">
              Locations can be managed from the <strong>Locations</strong> page. Administrators can:
            </p>
            <ul className="text-sm text-secondary-700 dark:text-secondary-300 list-disc list-inside ml-2">
              <li>Add new locations with name, address, city, state, and ZIP code</li>
              <li>Edit existing location details</li>
              <li>Delete locations that are no longer in use</li>
            </ul>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">Important Notes</h4>
            <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1 list-disc list-inside">
              <li>All data is stored in the cloud database and changes take effect immediately</li>
              <li>Be cautious when deleting key types or locations that may be referenced by existing inventory or assignments</li>
              <li>Use the Settings page to export data backups before making major changes</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Troubleshooting */}
      <Card>
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">Troubleshooting</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-secondary-900 dark:text-secondary-100">Key types or locations not showing</h3>
            <ul className="text-sm text-secondary-700 dark:text-secondary-300 mt-1 list-disc list-inside">
              <li>Clear browser cache and refresh (Ctrl+Shift+R)</li>
              <li>Check your network connection</li>
              <li>Verify the database is initialized (check Settings page)</li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium text-secondary-900 dark:text-secondary-100">Cannot assign keys</h3>
            <ul className="text-sm text-secondary-700 dark:text-secondary-300 mt-1 list-disc list-inside">
              <li>Ensure there are available keys (quantity &gt; assigned)</li>
              <li>Verify the person exists in the People list</li>
              <li>Check your user permissions</li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium text-secondary-900 dark:text-secondary-100">Data not saving</h3>
            <ul className="text-sm text-secondary-700 dark:text-secondary-300 mt-1 list-disc list-inside">
              <li>Check your network connection</li>
              <li>Verify the API is responding (check browser console for errors)</li>
              <li>Try refreshing the page and attempting again</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
